import { EXTERNAL_URLS } from '../config';

class SupportChatRequestError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'SupportChatRequestError';
    this.status = status;
  }
}

const parseResponse = async (response) => {
  const raw = await response.text();
  let payload = null;
  if (raw) {
    try {
      payload = JSON.parse(raw);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const message = payload?.message || payload?.error || raw.trim() || 'Não foi possível acessar o chat.';
    throw new SupportChatRequestError(message, response.status);
  }
  return payload;
};

const chatFetch = async (path, options = {}) => {
  const headers = {
    Accept: 'application/json',
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...options.headers,
  };
  const response = await fetch(`${EXTERNAL_URLS.apiBase}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  });
  return parseResponse(response);
};

const supportPresenceFetch = async (path) => {
  const response = await fetch(`${EXTERNAL_URLS.supportApiBase}${path}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });
  return parseResponse(response);
};

const RESUMABLE_STATUS_PRIORITY = [
  'in_progress',
  'waiting',
  'open',
  'active',
  'escalated',
];

const sessionActivityTime = (session) => {
  const timestamp = new Date(
    session?.lastMessageAt || session?.updatedAt || session?.createdAt || 0,
  ).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const activeSession = (sessions) => {
  if (!Array.isArray(sessions)) {
    return null;
  }
  const candidates = sessions.filter(
    (session) => session && RESUMABLE_STATUS_PRIORITY.includes(session.status),
  );
  if (candidates.length === 0) {
    return null;
  }
  return [...candidates].sort((left, right) => {
    const priorityDifference = RESUMABLE_STATUS_PRIORITY.indexOf(left.status)
      - RESUMABLE_STATUS_PRIORITY.indexOf(right.status);
    return priorityDifference || sessionActivityTime(right) - sessionActivityTime(left);
  })[0];
};

export const supportChatService = {
  async getOnlineAgents() {
    const payload = await supportPresenceFetch('/api/public/online-agents');
    return Array.isArray(payload?.data?.agents) ? payload.data.agents : [];
  },

  async getSupportAgentProfile(agentId) {
    const payload = await supportPresenceFetch(
      `/api/public/support-agents/${encodeURIComponent(agentId)}`,
    );
    return payload?.data?.agent || null;
  },

  async findAuthenticatedSession() {
    try {
      const payload = await chatFetch('/api/chat/sessions');
      return {
        authenticated: true,
        session: activeSession(payload?.data),
      };
    } catch (error) {
      if (error instanceof SupportChatRequestError && error.status === 401) {
        return { authenticated: false, session: null };
      }
      throw error;
    }
  },

  async createAuthenticatedSession() {
    const payload = await chatFetch('/api/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({
        category: 'general',
        subject: 'Atendimento pelo site',
      }),
    });
    return payload?.data;
  },

  async getAuthenticatedHistory(sessionId) {
    const payload = await chatFetch(`/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`);
    return payload?.data;
  },

  async markAuthenticatedMessagesRead(sessionId, messageIds) {
    if (!Array.isArray(messageIds) || messageIds.length === 0) {
      return null;
    }
    const payload = await chatFetch(
      `/api/chat/sessions/${encodeURIComponent(sessionId)}/messages/read`,
      {
        method: 'POST',
        body: JSON.stringify({ messageIds }),
      },
    );
    return payload?.data || null;
  },

  async sendAuthenticatedMessage(sessionId, content) {
    const payload = await chatFetch(`/api/chat/sessions/${encodeURIComponent(sessionId)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
    return payload?.data;
  },

  async findGuestSession() {
    try {
      const payload = await chatFetch('/api/public/chat/session');
      return payload?.data || null;
    } catch (error) {
      if (error instanceof SupportChatRequestError && error.status === 401) {
        return null;
      }
      throw error;
    }
  },

  async createGuestSession(email, phone) {
    const payload = await chatFetch('/api/public/chat/sessions', {
      method: 'POST',
      body: JSON.stringify({ email, phone }),
    });
    return payload?.data;
  },

  async getGuestHistory(sessionId) {
    const payload = await chatFetch(`/api/public/chat/sessions/${encodeURIComponent(sessionId)}/messages`);
    return payload?.data;
  },

  async sendGuestMessage(sessionId, content) {
    const payload = await chatFetch(`/api/public/chat/sessions/${encodeURIComponent(sessionId)}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
    return payload?.data;
  },
};

export { SupportChatRequestError };
